// ─────────────────────────────────────────────────────────────
// Shared Avatar component
//
// Single source of truth for avatar rendering across the app.
// Renders the image if available, falls back to coloured initials
// on load error or when no URL is provided.
//
// Used in: DiscussionCard, DiscussionDetailPage, UserHoverCard,
//          ProfilePage, SettingsPage, AdminPage, TopNav
// ─────────────────────────────────────────────────────────────
import { useState } from 'react'
import { getInitials, getAvatarColor } from '../../lib/avatar'

interface AvatarProps {
  name: string
  avatarUrl?: string | null
  size?: number
  onClick?: (e: React.MouseEvent) => void
}

export default function Avatar({ name, avatarUrl, size = 36, onClick }: AvatarProps) {
  const [imgFailed, setImgFailed] = useState(false)
  const showImg = !!avatarUrl && !imgFailed
  const cursorStyle = onClick ? 'pointer' : undefined

  return showImg ? (
    <img
      src={avatarUrl!}
      alt={name}
      onClick={onClick}
      style={{
        width: size, height: size, borderRadius: '50%',
        objectFit: 'cover', flexShrink: 0, display: 'block',
        cursor: cursorStyle,
        transition: 'opacity 0.15s',
      }}
      onError={() => setImgFailed(true)}
      onMouseEnter={(e) => { if (onClick) (e.currentTarget as HTMLImageElement).style.opacity = '0.8' }}
      onMouseLeave={(e) => { if (onClick) (e.currentTarget as HTMLImageElement).style.opacity = '1' }}
    />
  ) : (
    <div
      onClick={onClick}
      style={{
        width: size, height: size, borderRadius: '50%',
        flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: getAvatarColor(name),
        color: 'white', fontWeight: 700, fontSize: size * 0.38,
        cursor: cursorStyle,
        transition: 'opacity 0.15s',
      }}
      onMouseEnter={(e) => { if (onClick) (e.currentTarget as HTMLDivElement).style.opacity = '0.8' }}
      onMouseLeave={(e) => { if (onClick) (e.currentTarget as HTMLDivElement).style.opacity = '1' }}
    >
      {getInitials(name)}
    </div>
  )
}
